import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Box,
  Chip
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { 
  columnDefinitions, 
  calculateTotals, 
  sortData 
} from './dynamic-table-data';

export const DynamicTable = ({ 
  title, 
  data, 
  sortable = true, 
  showTotals = true,
  elevation = 2 
}) => {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('serialNo');

  const handleRequestSort = (property) => {
    if (!sortable) return;
    
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = sortable ? sortData(data, orderBy, order) : data;
  const totalsRow = showTotals ? calculateTotals(data) : null;

  const getStatusChip = (status) => {
    // Color coding based on status type
    if (status.includes('Done') || status.includes('Complete')) {
      return { color: 'success', variant: 'filled' };
    }
    if (status.includes('Deceased') || status.includes('Declined')) {
      return { color: 'error', variant: 'filled' };
    }
    if (status.includes('Busy') || status.includes('No Answer')) {
      return { color: 'warning', variant: 'outlined' };
    }
    if (status.includes('Invalid') || status.includes('Wrong')) {
      return { color: 'error', variant: 'outlined' };
    }
    if (status.includes('Above 2 Years') || status.includes('portal')) {
      return { color: 'info', variant: 'filled' };
    }
    return { color: 'default', variant: 'outlined' };
  };

  return (
    <Paper elevation={elevation} sx={{ p: 3, border: "1px solid #d0d0d0" }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        {title}
      </Typography>
      
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              {columnDefinitions.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{ 
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #e0e0e0'
                  }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  {sortable && column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                      {orderBy === column.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row, index) => (
              <TableRow 
                key={`${row.serialNo}-${index}`}
                sx={{ 
                  '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                  '&:hover': { backgroundColor: '#e3f2fd' }
                }}
              >
                <TableCell align="center">{row.serialNo}</TableCell>
                <TableCell align="left">
                  <Chip 
                    label={row.callStatus}
                    size="small"
                    {...getStatusChip(row.callStatus)}
                    sx={{ minWidth: 120 }}
                  />
                </TableCell>
                <TableCell align="right">{row.newRegistration.toLocaleString()}</TableCell>
                <TableCell align="right">{row.recentlyDelivered.toLocaleString()}</TableCell>
                <TableCell align="right">{row.followUp.toLocaleString()}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {row.total.toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
            
            {/* Totals Row */}
            {totalsRow && (
              <TableRow sx={{ 
                backgroundColor: '#e8f5e8',
                '&:hover': { backgroundColor: '#d4edda' },
                borderTop: '2px solid #28a745'
              }}>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>-</TableCell>
                <TableCell align="left">
                  <Chip 
                    label={totalsRow.callStatus}
                    size="small"
                    color="success"
                    variant="filled"
                    sx={{ fontWeight: 'bold', minWidth: 120 }}
                  />
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {totalsRow.newRegistration.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {totalsRow.recentlyDelivered.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {totalsRow.followUp.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                  {totalsRow.total.toLocaleString()}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Summary Information */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Records:</strong> {data.length} entries | 
          <strong> Total Records:</strong> {totalsRow ? totalsRow.total.toLocaleString() : 'N/A'} | 
          <strong> Sorting:</strong> {sortable ? 'Enabled' : 'Disabled'}
          {orderBy && sortable && (
            <span> | <strong>Current Sort:</strong> {columnDefinitions.find(col => col.id === orderBy)?.label} ({order})</span>
          )}
        </Typography>
      </Box>
    </Paper>
  );
};
